import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, of } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import * as AssignmentActions from './assignment.actions';
import { ASSIGNMENT_LIST_SORT } from './assignment-list.constants';
import { AssignmentDto } from '@shared/dto';
import { AssignmentService } from '../services/assignment.service';
import { AssignmentMemberService } from '../services/assignment-member.service';
import { selectAssignmentsQuery } from './assignment.selectors';
import { listDeletedParams, toHttpParams } from '@shared/utils/pagination.utils';

@Injectable()
export class AssignmentEffects {
  private readonly actions$ = inject(Actions);
  private readonly assignmentService = inject(AssignmentService);
  private readonly assignmentMemberService = inject(AssignmentMemberService);
  private readonly store = inject(Store);

  readAssignments$ = createEffect(() => this.actions$.pipe(
    ofType(AssignmentActions.readAssignments),
    exhaustMap(({ memberId, deleted }) => {
      const params = { ...toHttpParams(ASSIGNMENT_LIST_SORT), ...listDeletedParams(deleted) };
      const source$ = memberId != null
        ? this.assignmentMemberService.read({ memberId }, { params })
        : this.assignmentService.read(undefined, { params });
      return source$.pipe(
        map((data: AssignmentDto[]) => AssignmentActions.readAssignmentsSuccess({ data })),
        catchError((error) => of(AssignmentActions.readAssignmentsFailure({ error })))
      );
    })
  ));

  createAssignment$ = createEffect(() => this.actions$.pipe(
    ofType(AssignmentActions.createAssignment),
    exhaustMap(({ memberId, dto }) => this.assignmentMemberService.create(dto, { memberId }).pipe(
      map((assignment: AssignmentDto) => AssignmentActions.createAssignmentSuccess({ assignment })),
      catchError((error) => of(AssignmentActions.createAssignmentFailure({ error })))
    ))
  ));

  updateAssignment$ = createEffect(() => this.actions$.pipe(
    ofType(AssignmentActions.updateAssignment),
    exhaustMap(({ memberId, assignmentId, dto }) => this.assignmentMemberService.update({ memberId, assignmentId }, dto).pipe(
      map((assignment: Partial<AssignmentDto>) => AssignmentActions.updateAssignmentSuccess({ assignment })),
      catchError((error) => of(AssignmentActions.updateAssignmentFailure({ error })))
    ))
  ));

  deleteAssignment$ = createEffect(() => this.actions$.pipe(
    ofType(AssignmentActions.deleteAssignment),
    exhaustMap(({ memberId, assignmentId, confirm }) => this.assignmentMemberService.delete(
      { memberId, assignmentId },
      confirm ? { params: { confirm: 'true' } } : undefined,
    ).pipe(
      map((assignment: AssignmentDto) => AssignmentActions.deleteAssignmentSuccess({ assignment })),
      catchError((error) => {
        // 428 = therapy already started; not a failure, let the page confirm and resend
        // `confirm=true`. Any other status stays a failure — its message is surfaced by the toast.
        if (error?.status === HttpStatusCode.PreconditionRequired) {
          return of(AssignmentActions.deleteAssignmentRequiresConfirmation({
            memberId,
            assignmentId,
            message: error.message,
          }));
        }
        return of(AssignmentActions.deleteAssignmentFailure({ error }));
      })
    ))
  ));

  restoreAssignment$ = createEffect(() => this.actions$.pipe(
    ofType(AssignmentActions.restoreAssignment),
    exhaustMap(({ memberId, assignmentId }) => this.assignmentMemberService.restore({ memberId, assignmentId }).pipe(
      map((assignment: AssignmentDto) => AssignmentActions.restoreAssignmentSuccess({ assignment })),
      catchError((error) => of(AssignmentActions.restoreAssignmentFailure({ error })))
    ))
  ));

  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      AssignmentActions.createAssignmentSuccess,
      AssignmentActions.updateAssignmentSuccess,
      AssignmentActions.deleteAssignmentSuccess,
      AssignmentActions.restoreAssignmentSuccess,
    ),
    withLatestFrom(this.store.select(selectAssignmentsQuery)),
    map(([, query]) => {
      const memberId = query?.filters?.['memberId'];
      return AssignmentActions.readAssignments({
        memberId: typeof memberId === 'number' ? memberId : undefined,
        deleted: query?.deleted,
      });
    }),
  ));
}
